import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Avatar,
  Grid,
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import AdminTable from '../components/AdminTable';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { getAllLogos, createLogo, updateLogo, deleteLogo, bulkCreateLogos } from '../api/apiService';

const AdminLogos = () => {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logoToDelete, setLogoToDelete] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Bulk upload state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkPreviews, setBulkPreviews] = useState([]);

  const fetchLogos = async () => {
    setLoading(true);
    try {
      const response = await getAllLogos();
      setLogos(response.data || []);
    } catch (error) {
      console.error('Error fetching logos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogos();
  }, []);

  const columns = [
    { 
      id: 'image', 
      label: 'Logo', 
      render: (val) => (
        <Avatar 
          src={val} 
          variant="rounded" 
          sx={{ width: 60, height: 40, bgcolor: '#f5f5f5', '& img': { objectFit: 'contain' } }} 
        />
      ) 
    },
    { id: 'name', label: 'Brand Name' },
    { id: 'status', label: 'Status' },
  ];

  const handleOpen = (logo = null) => {
    if (logo) {
      setEditingLogo(logo);
      setPreviewImage(logo.image);
    } else {
      setEditingLogo({ name: '', status: 'Active' });
      setPreviewImage(null);
    }
    setSelectedFile(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingLogo(null);
    setPreviewImage(null);
    setSelectedFile(null);
  };

  const handleBulkOpen = () => {
    setBulkFiles([]);
    setBulkPreviews([]);
    setBulkOpen(true);
  };

  const handleBulkClose = () => {
    setBulkOpen(false);
    setBulkFiles([]);
    setBulkPreviews([]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBulkFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newFiles = [...bulkFiles, ...files];
      setBulkFiles(newFiles);

      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setBulkPreviews(prev => [...prev, { name: file.name, url: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeBulkFile = (index) => {
    const newFiles = [...bulkFiles];
    newFiles.splice(index, 1);
    setBulkFiles(newFiles);

    const newPreviews = [...bulkPreviews];
    newPreviews.splice(index, 1);
    setBulkPreviews(newPreviews);
  };

  const handleSubmit = async () => {
    if (!editingLogo.name) {
      alert('Brand name is required');
      return;
    }

    const formData = new FormData();
    formData.append('name', editingLogo.name);
    formData.append('status', editingLogo.status);
    if (selectedFile) {
      formData.append('image', selectedFile);
    }

    try {
      if (editingLogo.id) {
        await updateLogo(editingLogo.id, formData);
      } else {
        if (!selectedFile) {
          alert('Logo image is required for new entries');
          return;
        }
        await createLogo(formData);
      }
      fetchLogos();
      handleClose();
    } catch (error) {
      console.error('Error saving logo:', error);
      alert(error.response?.data?.message || 'Error saving logo');
    }
  };

  const handleBulkSubmit = async () => {
    if (bulkFiles.length === 0) {
      alert('Please select at least one file');
      return;
    }

    const formData = new FormData();
    bulkFiles.forEach(file => {
      formData.append('images', file);
    });

    try {
      await bulkCreateLogos(formData);
      fetchLogos();
      handleBulkClose();
    } catch (error) {
      console.error('Error bulk uploading logos:', error);
      alert(error.response?.data?.message || 'Error bulk uploading logos');
    }
  };

  const handleDelete = (logo) => {
    setLogoToDelete(logo);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (logoToDelete) {
      try {
        await deleteLogo(logoToDelete.id);
        fetchLogos();
        setDeleteDialogOpen(false);
        setLogoToDelete(null);
      } catch (error) {
        console.error('Error deleting logo:', error);
      }
    }
  };

  const filteredLogos = Array.isArray(logos) ? logos.filter((logo) =>
    logo.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={handleBulkOpen}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            px: 3,
            color: '#3C60B6',
            borderColor: '#3C60B6',
            '&:hover': { borderColor: '#2e4a8c', bgcolor: 'rgba(60, 96, 182, 0.04)' },
          }}
        >
          Bulk Upload
        </Button>
      </Box>

      <AdminTable
        title="Brand Logos Management"
        columns={columns}
        data={filteredLogos}
        onSearch={setSearchQuery}
        onAdd={() => handleOpen()}
        onEdit={(logo) => handleOpen(logo)}
        onDelete={handleDelete}
        showPagination={false}
      />

      {/* Single Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: '16px' } } }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {editingLogo?.id ? 'Edit Brand Logo' : 'Add New Brand Logo'}
          </Typography>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: '100%',
                  height: 150,
                  border: '2px dashed #e0e0e0',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: '#fafafa'
                }}
              >
                {previewImage ? (
                  <Box
                    component="img"
                    src={previewImage}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', color: '#999' }}>
                    <UploadIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="body2">Upload Brand Logo</Typography>
                  </Box>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                />
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Brand Name"
              variant="outlined"
              value={editingLogo?.name || ''}
              onChange={(e) => setEditingLogo({ ...editingLogo, name: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            
            <TextField
              fullWidth
              label="Status"
              select
              variant="outlined"
              value={editingLogo?.status || 'Active'}
              onChange={(e) => setEditingLogo({ ...editingLogo, status: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} sx={{ color: '#666', textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#3C60B6',
              '&:hover': { bgcolor: '#2e4a8c' },
              borderRadius: '10px',
              textTransform: 'none',
              px: 4,
              fontWeight: 600,
            }}
          >
            {editingLogo?.id ? 'Update Logo' : 'Save Logo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog
        open={bulkOpen}
        onClose={handleBulkClose}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: '16px' } } }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            Bulk Upload Brand Logos
          </Typography>
          <IconButton onClick={handleBulkClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ py: 2 }}>
            <Box
              sx={{
                width: '100%',
                py: 4,
                border: '2px dashed #e0e0e0',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                bgcolor: '#fafafa',
                mb: 3
              }}
            >
              <UploadIcon sx={{ fontSize: 48, color: '#3C60B6', mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#333' }}>Click or drag images here</Typography>
              <Typography variant="body2" color="textSecondary">Upload multiple logos at once (PNG, SVG, JPG)</Typography>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleBulkFileChange}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
            </Box>

            {bulkPreviews.length > 0 && (
              <Grid container spacing={2}>
                {bulkPreviews.map((preview, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Box
                      sx={{
                        position: 'relative',
                        border: '1px solid #eee',
                        borderRadius: '8px',
                        p: 1,
                        bgcolor: '#fff',
                        textAlign: 'center'
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => removeBulkFile(index)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: '#fff',
                          boxShadow: 1,
                          '&:hover': { bgcolor: '#f5f5f5' }
                        }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                      <Box
                        component="img"
                        src={preview.url}
                        sx={{ width: '100%', height: 60, objectFit: 'contain', mb: 1 }}
                      />
                      <Typography variant="caption" sx={{ display: 'block', noWrap: true, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {preview.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleBulkClose} sx={{ color: '#666', textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={handleBulkSubmit}
            variant="contained"
            disabled={bulkFiles.length === 0}
            sx={{
              bgcolor: '#3C60B6',
              '&:hover': { bgcolor: '#2e4a8c' },
              borderRadius: '10px',
              textTransform: 'none',
              px: 4,
              fontWeight: 600,
            }}
          >
            Upload {bulkFiles.length} {bulkFiles.length === 1 ? 'Logo' : 'Logos'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Brand Logo?"
        message={`Are you sure you want to delete "${logoToDelete?.name}"? This action cannot be undone.`}
      />
    </Box>
  );
};

export default AdminLogos;
